// Copyright (c) Microsoft. All rights reserved.

import {
    makeStyles,
    shorthands,
    tokens,
    Image,
    Menu,
    MenuTrigger,
    Avatar,
    MenuPopover,
    MenuList,
    MenuItem,
    NavDrawer,
    NavDrawerHeader,
    Tooltip,
    Persona,
    MenuDivider,
    mergeClasses,
} from '@fluentui/react-components';
import { Dispatch, FC, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { useAppSelector } from '../../../redux/app/hooks';
import { RootState } from '../../../redux/app/store';
import { Conversations } from '../../../redux/features/conversations/ConversationsState';
import { Breakpoints, customTokens } from '../../../styles';
import { ChatListSection } from './ChatListSection';
import appLogo from '../../../assets/app-icons/app-logo.png';
import { SimplifiedNewBotMenu } from './bot-menu/SimplifiedNewBotMenu';
import { FeatureKeys } from '../../../redux/features/app/AppState';
import { Settings24Regular, SignOut24Regular, ChevronUpDownFilled } from '@fluentui/react-icons';
import { useBreakpointTransition } from '../../../libs/hooks/useBreakpointTransition';
import closeButton from '../../../assets/action-icons/close-button.svg';
import { isToday } from '../../utils/TextUtils';
import { useMsal } from '@azure/msal-react';
import { AuthHelper } from '../../../libs/auth/AuthHelper';
import { resetState } from '../../../redux/app/store';
import { httpInterceptor } from '../../../libs/services/HttpInterceptorService';
import { SettingsDialog } from '../../header/settings-dialog/SettingsDialog';

const useClasses = makeStyles({
    root: {
        display: 'flex',
        flexShrink: 0,
        width: '292px',
        height: '100vh',
        backgroundColor: customTokens.colorChatRoomHeaderBackgroud,
        flexDirection: 'column',
        ...shorthands.overflow('hidden'),
        ...Breakpoints.small({
            display: 'none',
        }),
        ...shorthands.borderRight('1px', 'solid', customTokens.colorBorder),
        position: 'relative',
    },
    list: {
        overflowY: 'auto',
        overflowX: 'hidden',
        flex: '1 1 0',
        minHeight: 0,
        maxHeight: 'calc(100% - 76px)',
        paddingBottom: tokens.spacingVerticalM,
        '&:hover': {
            '&::-webkit-scrollbar-thumb': {
                backgroundColor: tokens.colorScrollbarOverlay,
                visibility: 'visible',
            },
        },
        '&::-webkit-scrollbar-track': {
            backgroundColor: tokens.colorSubtleBackground,
        },
        alignItems: 'stretch',
    },
    header: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        paddingRight: tokens.spacingHorizontalL,
        paddingLeft: tokens.spacingHorizontalL,
        alignItems: 'center',
        height: '64px',
        ...shorthands.borderBottom('1px', 'solid', customTokens.colorBorder),
        backgroundColor: customTokens.colorChatRoomHeaderBackgroud,
        boxSizing: 'border-box',
    },
    language: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    langButton: {
        maxWidth: '19px !important',
        padding: '0',
    },
    title: {
        flexGrow: 1,
        ...Breakpoints.small({
            display: 'none',
        }),
        fontSize: tokens.fontSizeBase500,
    },
    input: {
        flexGrow: 1,
        ...shorthands.padding(tokens.spacingHorizontalNone),
        ...shorthands.border(tokens.borderRadiusNone),
        backgroundColor: tokens.colorSubtleBackground,
        fontSize: tokens.fontSizeBase500,
    },
    persona: {
        ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingVerticalMNudge),
        overflowWrap: 'break-word',
    },
    personalInfo: {
        display: 'flex',
        padding: '8px',
        flexShrink: 0,
        minHeight: '48px',
        backgroundColor: customTokens.colorChatRoomHeaderBackgroud,
        ...shorthands.borderTop('1px', 'solid', customTokens.colorBorder),
        justifyContent: 'space-between',
    },
    avatar: {
        width: '36px',
        height: '36px',
        borderRadius: '50%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    avatarInfo: {
        display: 'flex',
        flexDirection: 'column',
    },
    personalAvatar: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 12px',
        borderRadius: tokens.borderRadiusXLarge,
        transition: 'all 0.2s ease-in-out',
        width: '100%',
        justifyContent: 'space-between',
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
        '&:active': {
            backgroundColor: tokens.colorNeutralBackground1Pressed,
        },
        ...Breakpoints.small({
            padding: '6px 8px',
        }),
    },
    avatarSection: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        flex: 1,
        minWidth: 0, // Bu overflow için gerekli
    },
    menuIndicator: {
        color: tokens.colorNeutralForeground3,
        transition: 'transform 0.2s ease-in-out',
    },
    avatarName: {
        fontSize: '14px',
        fontWeight: '500',
        color: customTokens.colorCustomText,
        lineHeight: '22px',
    },
    avatarMail: {
        color: customTokens.colorTextDisabled2,
        lineHeight: '20px',
        fontSize: '12px',
        maxWidth: '180px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        display: 'inline-block',
        cursor: 'pointer',
        ...Breakpoints.small({
            maxWidth: '150px',
        }),
    },
    modernMenuPopover: {
        minWidth: '280px',
        maxWidth: '320px',
        ...shorthands.padding(tokens.spacingVerticalM),
        borderRadius: tokens.borderRadiusLarge,
    },
    modernPersona: {
        padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalS}`,
        overflowWrap: 'break-word',
        borderRadius: tokens.borderRadiusMedium,
        marginBottom: tokens.spacingVerticalS,
    },
    modernMenuItem: {
        borderRadius: tokens.borderRadiusMedium,
        padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalS}`,
        minHeight: '44px',
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalM,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
    },
    menuItemIcon: {
        fontSize: '20px',
        color: tokens.colorNeutralForeground2,
        width: '20px',
        height: '20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    menuItemText: {
        fontSize: tokens.fontSizeBase300,
        fontWeight: tokens.fontWeightRegular,
        color: tokens.colorNeutralForeground1,
        lineHeight: '20px',
    },
    signOutMenuItem: {
        color: tokens.colorPaletteRedForeground1,
        '&:hover': {
            backgroundColor: customTokens.colorDangerHoverBackground,
        },
        '& .menuItemIcon': {
            color: tokens.colorPaletteRedForeground1,
        },
    },
    nav: {
        display: 'none',
        ...Breakpoints.small({
            display: 'flex',
            flexDirection: 'column',
            height: '100vh',
        }),
        backgroundColor: tokens.colorStrokeFocus1,
    },
    mobileHeader: {
        padding: '0 !important',
    },
    closeButton: {
        display: 'none',
        ...Breakpoints.small({
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        }),
    },
    appLogo: {
        filter: customTokens.actionButtonColorFilter,
    },
    menuContent: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
    },
    menuContentItem: {
        flexShrink: 0,
    },
    menuContentList: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
    },
    infoMessage: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
        backgroundColor: customTokens.colorInfoBackground,
        ...shorthands.borderTop('1px', 'solid', customTokens.colorInfoBorder),
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
        textAlign: 'center',
        lineHeight: '18px',
        flexShrink: 0,
    },
});

interface ConversationsView {
    latestConversations?: Conversations;
    olderConversations?: Conversations;
}

interface ChatListProps {
    isOpen: boolean;
    setIsOpen: Dispatch<SetStateAction<boolean>>;
    setLoadingState?: () => void;
}

interface ChatListBaseProps {
    selectedAgentId: string;
}

type CombinedChatListProps = ChatListProps & ChatListBaseProps;

export const ChatList: FC<CombinedChatListProps> = ({ selectedAgentId, isOpen, setIsOpen, setLoadingState }) => {
    const classes = useClasses();
    const { conversations } = useAppSelector((state: RootState) => state.conversations);
    const { activeUserInfo, features } = useAppSelector((state: RootState) => state.app);
    const { instance } = useMsal();

    const [openSettingsDialog, setOpenSettingsDialog] = useState(false);

    const onLogout = useCallback(() => {
        if (setLoadingState) {
            setLoadingState();
        }
        // Clear all tokens from interceptor on logout
        httpInterceptor.clearTokens();
        AuthHelper.logoutAsync(instance);
        resetState();
    }, [instance, setLoadingState]);

    const { isMobile } = useBreakpointTransition({
        breakpoint: 744,
        onEnterDesktop: () => {
            setIsOpen(false);
        },
    });

    //const [isFiltering, setIsFiltering] = useState(false);
    //const [filterText, setFilterText] = useState('');
    const [conversationsView, setConversationsView] = useState<ConversationsView>({
        latestConversations: conversations,
    });

    const sortConversations = (conversations: Conversations): ConversationsView => {
        // sort conversations by last activity
        const sortedIds = Object.keys(conversations).sort((a, b) => {
            const timestampA = conversations[a].lastUpdatedTimestamp;
            const timestampB = conversations[b].lastUpdatedTimestamp;
            // undefined değerler en sona gelsin
            if (typeof timestampA !== 'number' && typeof timestampB !== 'number') return 0;
            if (typeof timestampA !== 'number') return 1;
            if (typeof timestampB !== 'number') return -1;
            return timestampA - timestampB;
        });

        // Add conversations to sortedConversations in the order of sortedIds.
        const latestConversations: Conversations = {};
        const olderConversations: Conversations = {};
        sortedIds.forEach((id) => {
            const timestamp = conversations[id].lastUpdatedTimestamp;
            if (typeof timestamp === 'number' && isToday(new Date(timestamp))) {
                latestConversations[id] = conversations[id];
            } else {
                olderConversations[id] = conversations[id];
            }
        });
        return {
            latestConversations: latestConversations,
            olderConversations: olderConversations,
        };
    };

    useEffect(() => {
        // Ensure local component state is in line with app state.
        //const nonHiddenConversations: Conversations = {};
        /* for (const key in conversations) {
            const conversation = conversations[key];
            if (
                !filterText ||
                getFriendlyChatName(conversation).toLocaleUpperCase().includes(filterText.toLocaleUpperCase())
            ) {
                nonHiddenConversations[key] = conversation;
            }
        } */

        setConversationsView(sortConversations(conversations));
    }, [conversations]);

    /*    const onFilterClick = () => {
           setIsFiltering(true);
       };
   
       const onFilterCancel = () => {
           setFilterText('');
           setIsFiltering(false);
       };
   
       const onSearch = (ev: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
           ev.preventDefault();
           setFilterText(data.value);
       }; */

    const fileUploaderRef = useRef<HTMLInputElement>(null);
    /*  const onUpload = useCallback(
         (file: File) => {
             fileHandler.loadFile<ChatArchive>(file, chat.uploadBot).catch((error) =>
                 dispatch(
                     addAlert({
                         message: `Failed to parse uploaded file. ${error instanceof Error ? error.message : ''}`,
                         type: AlertType.Error,
                     }),
                 ),
             );
         },
         [fileHandler, chat, dispatch],
     ); */

    const menuHeaderContent = () => {
        return (
            <div className={classes.header}>
                {/* {features[FeatureKeys.SimplifiedExperience].enabled ? (
         <SimplifiedNewBotMenu onFileUpload={() => fileUploaderRef.current?.click()} />
     ) : (
         <>
             {!isFiltering && (
                 <>
                     <Subtitle2Stronger className={classes.title}>Conversations</Subtitle2Stronger>
                     <Button icon={<Filter20 />} appearance="transparent" onClick={onFilterClick} />
                     <NewBotMenu onFileUpload={() => fileUploaderRef.current?.click()} />
                     <FileUploader
                         ref={fileUploaderRef}
                         acceptedExtensions={['.json']}
                         onSelectedFile={onUpload}
                     />
                 </>
             )}
             {isFiltering && (
                 <>
                     <Input
                         placeholder="Filter by name"
                         className={mergeClasses(classes.input, classes.title)}
                         onChange={onSearch}
                         autoFocus
                     />
                     <Button icon={<Dismiss20 />} appearance="transparent" onClick={onFilterCancel} />
                 </>
             )}
         </>
     )} */}
                <div>
                    <Image alt="app-logo" src={appLogo} width={60} height={45} className={classes.appLogo} />
                </div>
                <div className={classes.language}>
                    {/* <span>EN</span>
                <span>TR</span> */}
                    <span className={classes.closeButton}>
                        <Tooltip content="Close" relationship="label">
                            <Image
                                src={closeButton}
                                onClick={() => {
                                    setIsOpen(!isOpen);
                                }}
                            />
                        </Tooltip>
                    </span>
                </div>
            </div>
        );
    };

    const menuContent = () => {
        return (
            <div className={classes.menuContent}>
                <div className={classes.menuContentItem}>
                    {/* {features[FeatureKeys.SimplifiedExperience].enabled && ( */}
                    <SimplifiedNewBotMenu
                        onFileUpload={() => fileUploaderRef.current?.click()}
                        selectedAgentId={selectedAgentId}
                    />
                    {/* )} */}
                </div>
                <div aria-label={'chat list'} className={classes.list}>
                    {conversationsView.latestConversations && (
                        <ChatListSection
                            header="Sohbetler"
                            conversations={{
                                ...conversationsView.latestConversations,
                                ...conversationsView.olderConversations,
                            }}
                            selectedAgentId={selectedAgentId}
                        />
                    )}
                </div>
                <div className={classes.infoMessage}>
                    📅 Mesajlarınız güvenlik politikamız gereği 7 gün sonra otomatik olarak silinir.
                </div>
                <div className={classes.personalInfo}>
                    <Menu
                        positioning={
                            isMobile
                                ? { position: 'above', offset: { mainAxis: 8, crossAxis: 0 } }
                                : { position: 'after', align: 'top', offset: { mainAxis: 12 } }
                        }
                    >
                        <MenuTrigger disableButtonEnhancement>
                            <div className={classes.personalAvatar}>
                                <div className={classes.avatarSection}>
                                    <Avatar
                                        className={classes.avatar}
                                        key={activeUserInfo?.username}
                                        name={activeUserInfo?.username}
                                        size={36}
                                        badge={
                                            !features[FeatureKeys.SimplifiedExperience].enabled
                                                ? { status: 'available' }
                                                : undefined
                                        }
                                        data-testid="userSettingsButton"
                                    />
                                    <div className={classes.avatarInfo}>
                                        <span className={classes.avatarName}>{activeUserInfo?.username}</span>
                                        <span className={classes.avatarMail}>{activeUserInfo?.email}</span>
                                    </div>
                                </div>
                                <ChevronUpDownFilled className={classes.menuIndicator} />
                            </div>
                        </MenuTrigger>
                        <MenuPopover className={classes.modernMenuPopover}>
                            <MenuList>
                                <Persona
                                    className={classes.modernPersona}
                                    name={activeUserInfo?.username}
                                    primaryText={activeUserInfo?.username}
                                    secondaryText={activeUserInfo?.email}
                                    presence={
                                        !features[FeatureKeys.SimplifiedExperience].enabled
                                            ? { status: 'available' }
                                            : undefined
                                    }
                                    avatar={{ color: 'colorful', size: 36 }}
                                />
                                <MenuDivider />

                                <MenuItem
                                    className={classes.modernMenuItem}
                                    data-testid="settingsMenuItem"
                                    onClick={() => {
                                        setOpenSettingsDialog(true);
                                    }}
                                    icon={<Settings24Regular />}
                                >
                                    <div className={classes.menuItemText}>Ayarlar</div>
                                </MenuItem>
                                <MenuDivider />
                                <MenuItem
                                    className={mergeClasses(classes.modernMenuItem, classes.signOutMenuItem)}
                                    data-testid="logOutMenuButton"
                                    onClick={onLogout}
                                    icon={<SignOut24Regular />}
                                >
                                    <div className={classes.menuItemText}>Çıkış Yap</div>
                                </MenuItem>
                            </MenuList>
                        </MenuPopover>
                    </Menu>
                    <SettingsDialog
                        open={openSettingsDialog}
                        closeDialog={() => {
                            setOpenSettingsDialog(false);
                        }}
                    />
                </div>
            </div>
        );
    };

    return (
        <div className={classes.root}>
            <NavDrawer
                // defaultSelectedValue="2"
                // defaultSelectedCategoryValue=""
                open={isOpen}
                onOpenChange={(_, { open }) => {
                    setIsOpen(open);
                }}
                className={classes.nav}
            >
                <NavDrawerHeader className={classes.mobileHeader}>{menuHeaderContent()}</NavDrawerHeader>
                <div className={classes.menuContentList}>{menuContent()}</div>
            </NavDrawer>
            {menuHeaderContent()}
            <div className={classes.menuContentList}>{menuContent()}</div>
        </div>
    );
};
